import React, { useCallback, useEffect, useMemo } from 'react';
import c from 'classnames';
import {

  AnimatePresence,
  motion,
  MotionConfig,
  type HTMLMotionProps,

} from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { FormRadioButtons } from '@uncinc/uncinc-react-kitchen-sink';

import { useWebformContext } from '../../../../context/WebformContext';
import InformationOverlay from '../../../InformationOverlay';
import type { WebformOptions } from '../../types';
import WebformAssessmentCount from '../Count';

import './style.scss';

// Type assertion needed for framer-motion compatibility with newer React types.
const MotionDiv = motion.div as unknown as React.FC<HTMLMotionProps<'div'>>;

export interface WebformAssessmentRadioButtonsProps extends Record<string, any> {
  entity: ImmutableEntity<WebformOptions>;
  touched: Record<string, boolean>,
  errors: Record<string, string>,
  values: Record<string, string>,
}

const WebformAssessmentRadioButtons: React.FC<WebformAssessmentRadioButtonsProps> = (props) => {
  const {
    entity,
    touched,
    errors,
    values,
    ...fieldProps
  } = props;

  const { t } = useTranslation();
  const { updateCurrentElement } = useWebformContext();

  const webform = entity.toJS();
  const key = webform['#webform_key'];
  const title = webform['#title'];
  const required = webform['#required'];
  const description = webform['#description'];
  const helpTitle = webform['#help_title'];
  const helpDescription = webform['#help'];
  const requiredError = webform['#required_error'];
  const layoutOptions = webform['#options_display'] || null;
  const wrapperAttributes = webform['#wrapper_attributes'];
  const wrapperClassNames = c('WebformAssessmentRadios', wrapperAttributes?.class[0] || '', {
    [`WebformAssessmentRadios--${String(layoutOptions)}`]: layoutOptions,
  });
  const classNames = webform['#attributes']?.class || [];
  const elementClassNames = c(classNames);
  const webformOptions = useMemo(() => webform['#options'] || {}, [webform]);

  const options = useMemo(() => Object.fromEntries(
    Object.entries(webformOptions).map(([optionKey, option]) => {
      if (option.split(' -- ').length > 1) {
        const optionValue = option.split(' -- ')[0];
        return [optionKey, optionValue];
      }
      return [optionKey, option];
    }),
  ), [webformOptions]);

  const descriptions = useMemo(() => Object.fromEntries(
    Object.entries(webformOptions).map(([optionKey, option]) => {
      if (option.split(' -- ').length > 1) {
        const optionDescription = option.split(' -- ')[1];
        return [optionKey, optionDescription];
      }
      return [optionKey, null];
    }),
  ), [webformOptions]);

  const validation = useCallback((value: string) => {
    let error;
    if (required && !value) {
      error = requiredError || t(['webform.components.radio.required', 'webform.required', 'webform_required']);
    }
    return error;
  }, [required, requiredError, t]);

  useEffect(() => {
    updateCurrentElement(key);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={wrapperClassNames}>
      <MotionConfig transition={{ duration: 0.3 }}>
        <AnimatePresence initial={false} mode="wait">
          <MotionDiv
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="WebformAssessmentRadios__header">
              <WebformAssessmentCount />

              {(helpTitle || helpDescription) && (
                <div className="Webform__help WebformRadios__help">
                  <InformationOverlay
                    overlayKey="Overlay_WebformRadios"
                    title={helpTitle}
                    body={helpDescription}
                    buttonIcon="info_alt"
                    buttonClassName="Button--secondary"
                  />
                </div>
              )}
            </div>

            <FormRadioButtons
              {...fieldProps}
              className={elementClassNames}
              title={title}
              required={required}
              description={description}
              fieldName={key}
              validation={validation}
              touched={touched[key]}
              error={errors[key]}
              value={values[key]}
              options={options}
              descriptions={descriptions}
            />
          </MotionDiv>
        </AnimatePresence>
      </MotionConfig>
    </div>
  );
};

export default WebformAssessmentRadioButtons;
